import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { contact, socialLinks } from '../constants';

const services = [
  'Website Development',
  'SaaS Development',
  'Business Applications',
  'Automation Solutions',
];

const quickLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Portfolio', href: '/portfolio' },
  { label: 'Testimonials', href: '/testimonials' },
  { label: 'Contact', href: '/contact' },
];


const Footer = () => {
  return (
    <footer className="bg-[linear-gradient(180deg,#1C1B1F_0%,#3B1F2B_100%)] text-white">
      <motion.section
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
        className="mx-auto max-w-7xl px-6 py-16 lg:px-8"
      >
        <div className="rounded-[36px] border border-white/10 bg-white/5 p-8 text-center shadow-glow backdrop-blur-xl sm:p-12">
          <p className="text-sm uppercase tracking-[0.35em] text-textSecondary">Ready to build your next big product?</p>
          <h2 className="mt-4 text-3xl font-semibold leading-tight text-white sm:text-4xl lg:text-5xl">
            Launch your premium SaaS experience with Genius KODS.
          </h2>
          <motion.div whileHover={{ y: -2 }} className="mt-8 flex justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center rounded-full bg-primary px-8 py-4 text-sm font-semibold text-slate-950 transition hover:bg-primaryLight hover:shadow-[0_0_20px_rgba(201,162,74,0.5)]"
            >
              Get Started
            </Link>
          </motion.div>
        </div>
      </motion.section>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.25 }}
        transition={{ duration: 0.9, ease: 'easeOut' }}
        className="mx-auto max-w-7xl space-y-12 px-6 pb-10 lg:px-8"
      >
        <div className="grid gap-10 md:grid-cols-2 xl:grid-cols-4">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-3 rounded-3xl border border-white/10 bg-white/5 p-3 shadow-glow">
              <img src="/logo.png" alt="Genius KODS logo" className="h-12 w-12 rounded-2xl object-cover" />
              <span className="text-lg font-semibold uppercase tracking-[0.24em] text-white">Genius KODS</span>
            </div>
            <p className="max-w-sm text-sm leading-7 text-textSecondary">
              Building scalable digital products for modern businesses. We design, develop, and deliver high-performance software solutions.
            </p>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Services</h3>
            <div className="space-y-3">
              {services.map((service) => (
                <motion.span
                  key={service}
                  whileHover={{ x: 4 }}
                  className="block cursor-pointer text-sm text-textSecondary transition hover:text-primary"
                >
                  {service}
                </motion.span>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Quick Links</h3>
            <div className="space-y-3">
              {quickLinks.map((link) => (
                <motion.div key={link.label} whileHover={{ x: 4 }}>
                  <Link to={link.href} className="text-sm text-textSecondary transition hover:text-primary">
                    {link.label}
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white">Contact</h3>
            <div className="space-y-3 text-sm text-textSecondary">
              <div className='flex'>
                <p>
                  <span className="text-white">Email:</span>
                </p>
                <p className='flex flex-col ml-2'>
                  {contact.email.map(email => (
                    <span key={email}>{email}</span>
                  ))}
                </p>
              </div>
              <div className='flex'>
                <p>
                  <span className="text-white">Phone:</span>
                </p>
                <p className='flex flex-col ml-2'>
                  {contact.phone.map(phone => (
                    <span key={phone}>{phone}</span>
                  ))}
                </p>
              </div>
              
              <p>
                <span className="text-white">Location:</span> {contact.location}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              {socialLinks.map(({ label, url, icon: Icon }) => (
                <motion.a
                  key={label}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  whileHover={{ scale: 1.05 }}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/5 text-textSecondary transition hover:border-primary hover:text-primary hover:shadow-[0_0_18px_rgba(201,162,74,0.35)]"
                  aria-label={label}
                >
                  <Icon size={18} />
                </motion.a>
              ))}
            </div>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6">
          <div className="flex flex-col items-center justify-between gap-4 text-sm text-textSecondary md:flex-row">
            <p>© 2026 Genius KODS. All rights reserved.</p>
            <div className="flex flex-wrap items-center justify-center gap-3 text-white/80">
              <Link to="/privacy" className="transition hover:text-primary">
                Privacy Policy
              </Link>
              <span className="hidden md:inline">|</span>
              <Link to="/terms" className="transition hover:text-primary">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </motion.div>
    </footer>
  );
};

export default Footer;
